import { useEffect, useId, useRef, useState, type KeyboardEvent } from 'react';
import styled, { css } from 'styled-components';
import { popIn } from '@shared/theme/animations';
import { IconSparkles } from '@shared/ui/icons';
import { SrOnly } from './ui';

/** Подсказка к каждому предложению ИИ-помощника — одна на весь раздел. */
export const AI_HINT = 'Предложение ИИ проверено программой. Цифры считает программа.';

type Align = 'start' | 'center' | 'end';
type Side = 'below' | 'above';

/** Ширина подсказки, зазор до бейджа, отступ от краёв окна, оценка высоты — px. */
const TIP_W = 248;
const TIP_GAP = 6;
const EDGE = 12;
const TIP_H = 64;

/** Общая геометрия маленьких бейджей происхождения значения. */
const pill = css`
  display: inline-flex;
  align-items: center;
  gap: ${({ theme }) => theme.spacing(1)};
  min-height: 22px;
  padding: 0 ${({ theme }) => theme.spacing(2)};
  border: 1px solid;
  border-radius: ${({ theme }) => theme.radius.pill};
  font-size: 11.5px;
  font-weight: 600;
  line-height: 1.2;
  white-space: nowrap;
  vertical-align: middle;
`;

/** Источник значения, не ИИ: «штамп РД», «словарь», «вручную»; `$muted` — «по умолчанию». */
export const SourceTag = styled.span<{ $muted?: boolean }>`
  ${pill}
  color: ${({ theme }) => theme.colors.text.secondary};
  ${({ theme, $muted }) =>
    $muted
      ? css`
          border-style: dashed;
          border-color: ${theme.colors.borderStrong};
          background: transparent;
          font-weight: 500;
        `
      : css`
          border-color: ${theme.colors.border};
          background: ${theme.colors.surface};
        `}
`;

const Wrap = styled.span`
  position: relative;
  display: inline-flex;
  vertical-align: middle;
`;

/* Текст — цветом info на фоне карточки: так контраст держит AA и в светлой теме. */
const Pill = styled.span`
  ${pill}
  border-color: ${({ theme }) => theme.colors.status.info}8C;
  background: ${({ theme }) => theme.colors.backgroundElevated};
  color: ${({ theme }) => theme.colors.status.info};
  cursor: help;

  svg {
    flex-shrink: 0;
  }
  /* Глобальный focus-visible скругляет до xs — бейдж остаётся «таблеткой». */
  &:focus-visible {
    border-radius: ${({ theme }) => theme.radius.pill};
  }
`;

/* Уточнение — обычным текстом после бейджа: в узкой строке переносится, а не вылезает. */
const Detail = styled.span`
  font-size: 12px;
  line-height: 1.4;
  color: ${({ theme }) => theme.colors.text.secondary};
`;

const Tip = styled.span<{ $align: Align; $side: Side }>`
  position: absolute;
  z-index: ${({ theme }) => theme.z.overlay};
  width: max-content;
  max-width: min(${TIP_W}px, calc(100vw - ${EDGE * 2}px));
  padding: ${({ theme }) => `${theme.spacing(2)} ${theme.spacing(3)}`};
  border-radius: ${({ theme }) => theme.radius.md};
  background: ${({ theme }) => theme.colors.text.primary};
  color: ${({ theme }) => theme.colors.backgroundElevated};
  box-shadow: ${({ theme }) => theme.shadow.md};
  font-size: 12.5px;
  font-weight: 500;
  line-height: 1.45;
  white-space: normal;
  text-wrap: pretty;
  animation: ${popIn} ${({ theme }) => theme.motion.fast} ${({ theme }) => theme.motion.easeOut};

  ${({ $side }) =>
    $side === 'below'
      ? css`
          top: calc(100% + ${TIP_GAP}px);
        `
      : css`
          bottom: calc(100% + ${TIP_GAP}px);
        `}
  ${({ $align }) =>
    $align === 'start'
      ? css`
          left: 0;
        `
      : $align === 'end'
        ? css`
            right: 0;
          `
        : css`
            left: 50%;
            translate: -50% 0;
          `}

  /* Мостик над зазором: указатель переходит с бейджа на подсказку, не закрывая её. */
  &::before {
    content: '';
    position: absolute;
    left: 0;
    right: 0;
    height: ${TIP_GAP}px;
    ${({ $side }) => ($side === 'below' ? 'top' : 'bottom')}: -${TIP_GAP}px;
  }
`;

/** Сторона и выравнивание, при которых подсказка помещается в окно (без координат в стилях). */
function placeTip(el: HTMLElement): { align: Align; side: Side } {
  const r = el.getBoundingClientRect();
  const vw = document.documentElement.clientWidth;
  const w = Math.min(TIP_W, vw - EDGE * 2);
  const overflow = (left: number) => Math.max(0, EDGE - left) + Math.max(0, left + w - (vw - EDGE));
  const options: ReadonlyArray<{ align: Align; left: number }> = [
    { align: 'start', left: r.left },
    { align: 'end', left: r.right - w },
    { align: 'center', left: r.left + r.width / 2 - w / 2 },
  ];
  const best = options.reduce((a, b) => (overflow(b.left) < overflow(a.left) ? b : a));
  const roomBelow = window.innerHeight - r.bottom;
  const side: Side = roomBelow >= TIP_H + TIP_GAP || r.top < TIP_H + TIP_GAP ? 'below' : 'above';
  return { align: best.align, side };
}

/**
 * Бейдж «ИИ-помощник» — единый для всего раздела. Подсказка открывается
 * наведением, фокусом с клавиатуры и касанием; Escape её закрывает (WCAG 1.4.13).
 * Для скринридера текст подсказки — описание бейджа (`aria-describedby`).
 * `detail` («найдено в тексте листа») — обычным текстом сразу после бейджа.
 * Не класть внутрь контейнеров с `overflow: auto/hidden` — подсказку обрежет.
 */
export function AiBadge({ detail }: { detail?: string }) {
  const hintId = useId();
  const ref = useRef<HTMLSpanElement>(null);
  const [hover, setHover] = useState(false);
  const [focus, setFocus] = useState(false);
  const [dismissed, setDismissed] = useState(false);
  const [place, setPlace] = useState<{ align: Align; side: Side }>({
    align: 'start',
    side: 'below',
  });
  const open = (hover || focus) && !dismissed;

  // Escape закрывает и подсказку, открытую наведением (фокус может быть где угодно).
  useEffect(() => {
    if (!open) return;
    const onKey = (e: globalThis.KeyboardEvent) => {
      if (e.key === 'Escape') setDismissed(true);
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open]);

  function measure() {
    if (ref.current) setPlace(placeTip(ref.current));
  }

  return (
    <>
      <Wrap
        onMouseEnter={() => {
          measure();
          setHover(true);
        }}
        onMouseLeave={() => {
          setHover(false);
          if (!focus) setDismissed(false);
        }}
      >
        <Pill
          ref={ref}
          tabIndex={0}
          aria-describedby={hintId}
          onFocus={() => {
            measure();
            setFocus(true);
          }}
          onBlur={() => {
            setFocus(false);
            if (!hover) setDismissed(false);
          }}
          onClick={() => {
            measure();
            setDismissed(false);
          }}
          onKeyDown={(e: KeyboardEvent<HTMLSpanElement>) => {
            if (e.key === 'Escape' && open) {
              e.stopPropagation();
              setDismissed(true);
            }
          }}
        >
          <IconSparkles size={12} />
          ИИ-помощник
        </Pill>
        <SrOnly id={hintId}>{AI_HINT}</SrOnly>
        {open && (
          <Tip $align={place.align} $side={place.side} aria-hidden="true">
            {AI_HINT}
          </Tip>
        )}
      </Wrap>
      {detail && <Detail>{detail}</Detail>}
    </>
  );
}
